import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Stack, Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LeadGenerationContext from '../../context/LeadGenerationContext';
import { CustomInput } from '../LeadGeneration/CustomInput/CustomInput';
import {
  LAST_NAME,
  FIRST_NAME,
  REQUIRED,
  EMAIL,
  MOBILE,
  CONTINUE,
  CONTACT_DISCLAIMER_BUNDLE,
  LIBRE_AHORRA_BUNDLE,
  BUNDLE_SUBTEXT,
  PREFERRED_TIME,
} from '../../constants';

import { CustomSelect } from '../LeadGeneration/CustomSelect/CustomSelect';
import { MobileInput } from '../LeadGeneration/MobileInput/MobileInput';
import { BundleContactStyles as classes } from './BundleContactStyles';
import Loader from '../Loader/Loader';
import { InfoIcon } from '../../images/svg/CustomizedSVG';
import PersonalInformation from '../LeadGeneration/StepOne/PersonalInformation';
import { isBundle, handleTrimmedBlur } from '../commonCode';

function BundleContact(props) {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const {
    contactInformation,
    contactInformationErrors,
    setFirstName,
    location,
    personalInformation,
    theme,
    vouchedData,
    productName,
  } = leadGenerationContext;
  const { handleChange, isValidating, handleContinue } = props;

  return (
    <Grid
      container
      spacing={3}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      data-page="contact-info"
    >
      {/* <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={classes.contentContainer}> */}
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
        <Typography variant="h3" sx={classes.unity_account_heading}>
          {t(LIBRE_AHORRA_BUNDLE, { defaultValue: LIBRE_AHORRA_BUNDLE })}
        </Typography>
        <Typography variant="subtitle1" sx={classes.confirm_details_subheading}>
          {t(BUNDLE_SUBTEXT, {
            defaultValue: BUNDLE_SUBTEXT,
          })}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={classes.gridStyles}>
        <CustomInput
          id="firstName"
          name="firstName"
          dataTestId="firstName_id"
          isBundle
          inputLabelAriaLabel={`${t(FIRST_NAME, {
            defaultValue: FIRST_NAME,
          })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
          label={
            <span style={{ color: contactInformationErrors?.firstName ? 'red' : 'inherit' }}>
              {t(FIRST_NAME, { defaultValue: FIRST_NAME })}
            </span>
          }
          autoFocus
          handleChange={handleChange}
          handleBlur={(e) =>
            handleTrimmedBlur(e, handleChange, () =>
              setFirstName(contactInformation?.firstName ? contactInformation.firstName.trim() : '')
            )
          }
          disabled={
            personalInformation?.firstName !== null &&
            (personalInformation?.uploadSuccessStatus ||
              leadGenerationContext.isApplicationSaved ||
              (leadGenerationContext.savedDataFlow && !isBundle(productName)))
          }
          placeholder={t('Enter First Name', {
            defaultValue: 'Enter First Name',
          })}
          fullWidth
          inputProps={{ maxLength: 10, 'data-testid': 'first-name' }}
          value={contactInformation?.firstName ? contactInformation.firstName : ''}
          error={contactInformationErrors?.firstName ? contactInformationErrors.firstName : ''}
        />
        <Box
          component="span"
          sx={classes.error_message}
          role="alert"
          data-testid="first-name-error"
        >
          {t(contactInformationErrors?.firstName, {
            defaultValue: contactInformationErrors?.firstName,
          })}
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={classes.gridStyles}>
        <CustomInput
          id="lastName"
          name="lastName"
          dataTestId="lastName_id"
          isBundle
          inputLabelAriaLabel={`${t(LAST_NAME)} ${t(REQUIRED)}`}
          label={
            <span style={{ color: contactInformationErrors?.lastName ? 'red' : 'inherit' }}>
              {t(LAST_NAME, { defaultValue: LAST_NAME })}
            </span>
          }
          handleChange={handleChange}
          handleBlur={(e) => handleTrimmedBlur(e, handleChange)}
          disabled={
            (personalInformation?.uploadSuccessStatus && vouchedData?.lastName !== null) ||
            // leadGenerationContext.isApplicationSaved
            // ||
            (leadGenerationContext.savedDataFlow && personalInformation?.lastName !== '')
          }
          placeholder={t('Enter Last Name', {
            defaultValue: 'Enter Last Name',
          })}
          fullWidth
          inputProps={{ maxLength: 15, 'data-testid': 'last-name' }}
          value={contactInformation?.lastName || ''}
          error={contactInformationErrors?.lastName}
        />
        <Box component="span" sx={classes.error_message} role="alert" data-testid="last-name-error">
          {t(contactInformationErrors?.lastName, {
            defaultValue: contactInformationErrors?.lastName,
          })}
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={classes.gridStyles}>
        <CustomInput
          id="email"
          dataTestId="email_id"
          isBundle
          disabled={
            leadGenerationContext.isApplicationSaved ||
            (leadGenerationContext.savedDataFlow && contactInformation.email !== null)
          }
          inputLabelAriaLabel={`${t(EMAIL)} ${t(REQUIRED)}`}
          label={
            <span style={{ color: contactInformationErrors?.email ? 'red' : 'inherit' }}>
              {t(EMAIL, { defaultValue: EMAIL })}
            </span>
          }
          handleChange={(e) => handleChange({ target: { name: 'email', value: e.target.value } })}
          placeholder={t('Enter Email', {
            defaultValue: 'Enter Email',
          })}
          fullWidth
          inputProps={{ 'data-testid': 'email', maxLength: 40 }}
          value={contactInformation?.email || ''}
          error={contactInformationErrors?.email}
        />
        <Box component="span" role="alert" sx={classes.error_message} data-testid="email-error">
          {t(contactInformationErrors?.email, {
            defaultValue: contactInformationErrors?.email,
          })}
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={classes.gridStyles}>
        <MobileInput
          id="phone"
          dataTestId="phone_id"
          isBundle
          ariaLabelForInputLabel={`${t(MOBILE)} ${t(REQUIRED)}`}
          label={
            <span style={{ color: contactInformationErrors?.phone ? '#FD0000' : 'inherit' }}>
              {t(MOBILE, { defaultValue: MOBILE })}
            </span>
          }
          location={location || 'PR'}
          handleChange={(e) => {
            const { name, value } = e.target;
            handleChange({ target: { name, value } });
          }}
          placeholder={t('Enter Mobile Number', {
            defaultValue: 'Enter Mobile Number',
          })}
          fullWidth
          phoneValue={contactInformation?.phone || ''}
          maxLength={location === 'VI' ? 10 : 7}
          inputMode="numeric"
          phoneError={contactInformationErrors?.phone}
          maxLengthOfAreaCode={3}
          areaCodeInputMode="numeric"
          areaCodeAriaLabel={`${t('Area Code')} ${t(REQUIRED)}`}
          areaCodeId="areaCode"
          areaCode={contactInformation?.areaCode || ''}
        />
        {contactInformationErrors?.areaCode ? (
          <Box
            component="span"
            role="alert"
            sx={classes.error_message}
            data-testid="areacode-error"
          >
            {t(contactInformationErrors?.areaCode, {
              defaultValue: contactInformationErrors?.areaCode,
            })}
          </Box>
        ) : (
          <Box component="span" role="alert" sx={classes.error_message} data-testid="phone-error">
            {t(contactInformationErrors?.phone, {
              defaultValue: contactInformationErrors?.phone,
            })}
          </Box>
        )}
      </Grid>

      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={classes.gridStyles}>
        <CustomSelect
          id="preferredContactTime"
          dataTestId="select_testid"
          isBundle
          inputLabelAriaLabel={`${t(PREFERRED_TIME)} ${t(REQUIRED)}`}
          label={t(PREFERRED_TIME, {
            defaultValue: PREFERRED_TIME,
          })}
          options={['8:00 AM to 12:00 PM', '12:00 PM to 6:00 PM']}
          nsSeparator="|"
          handleChange={handleChange}
          fullWidth
          value={contactInformation?.preferredContactTime || ''}
          selectAriaLabel={t('Aria Label Preferred Contact Time', {
            defaultValue:
              'Preferred Contact Time Select Preferred Contact Time dropdown menu button',
          })}
        />
        <Box component="span" role="alert" sx={classes.error_message}>
          {t(contactInformationErrors?.preferredContactTime, {
            defaultValue: contactInformationErrors?.preferredContactTime,
          })}
        </Box>
      </Grid>

      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={classes.consent_container}>
        {/* <InfoOutlinedIcon sx={classes.info_tooltip_icon} /> */}
        <Box component="span" sx={{ width: '20px !important', height: '20px !important' }}>
          <InfoIcon width="20px" height="20px" color={theme?.palette?.orientalPrimary?.main} />
        </Box>
        <Typography
          id="contactDisclaimerId"
          component="p"
          variant="body1"
          color="subtext"
          // sx={classes.consent_text}
        >
          {t(CONTACT_DISCLAIMER_BUNDLE, {
            defaultValue: CONTACT_DISCLAIMER_BUNDLE,
          })}
        </Typography>
      </Grid>
      {/* <Button
          disableFocusRipple
          sx={{
            ...classes.continueButton,
            background: theme?.palette?.orientalPrimary?.main,
            color: theme?.palette?.secondary?.main,
            textTransform: 'capitalize',
            // '&:disabled': {
            //   backgroundColor: '#f3f3f3',
            // },
          }}
          data-testid="CloseButtonInProductModal"
          onClick={handleContinue}
          // disabled={!isFormComplete}
        >
          {t(CONTINUE, { defaultValue: CONTINUE, nsSeparator: '|' })}
        </Button> */}
    </Grid>
    // </Grid>
  );
}
export default BundleContact;

BundleContact.propTypes = {
  handleChange: PropTypes.func,
  isValidating: PropTypes.bool,
  handleContinue: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
};
